const KEY = 'page-scroll';

const HEIGHT_KEY = 'page-height';

const keyFor = (path) => `${KEY}:${path.replace(/\/+$/, '') || '/'}`;
// How tall this page turned out to be, read by the same blocking script.
//
// Not a position, and it does not follow a position's rules. An offset belongs
// to one visit and is forgotten on the way out. A height is a fact about the
// page that stays true, and it is wanted most on a load that has no position at
// all: the placeholders standing in for the live content are not the size of
// what replaces them, so the document resizes by hundreds of pixels partway
// through the load. On a phone that is what makes Chrome reconsider the address
// bar, and the address bar moving takes the whole page with it.
const heightKeyFor = (path) => `${HEIGHT_KEY}:${path.replace(/\/+$/, '') || '/'}`;

export function usePageScroll() {
  const route = useRoute();
  let ownKey = null;
  let timer = null;

  function saveHeight() {
    try {
      sessionStorage.setItem(
        heightKeyFor(route.path),
        String(Math.round(document.documentElement.scrollHeight))
      );
    } catch {
      // private mode / storage disabled
    }
  }

  function save() {
    if (!ownKey) return;
    try {
      sessionStorage.setItem(ownKey, String(Math.round(window.scrollY)));
    } catch {
      // private mode / storage disabled
    }
    saveHeight();
  }

  function onScroll() {
    clearTimeout(timer);
    timer = setTimeout(save, 150);
  }

  onMounted(() => {
    ownKey = keyFor(route.path);
    window.addEventListener('scroll', onScroll, { passive: true });

    // Recorded even if nobody scrolls, which is the case that was missing: the
    // height only ever reached storage from a scroll handler, so a page read at
    // the top and reloaded had nothing to hold it steady the second time.
    //
    // Once the live content has landed and the pictures are in, so what is
    // written is the height the page settles at rather than the one it passes
    // through.
    const settle = () => setTimeout(saveHeight, 400);
    if (document.readyState === 'complete') settle();
    else window.addEventListener('load', settle, { once: true });
  });

  // Forgotten on the way out, not saved.
  //
  // The offset is only meant to survive a reload, and this does not run on one:
  // leaving the page is the only thing that unmounts it. Keeping the value here
  // meant the position outlived the visit, so opening the page fresh later
  // dropped you back where a previous visit had ended.
  //
  // Going back to it still lands correctly, because that is a popstate and the
  // router restores it from savedPosition.
  onBeforeUnmount(() => {
    clearTimeout(timer);
    // The height is kept. It is the last thing this page knows about itself and
    // the next load wants it before anything else has run.
    saveHeight();
    try {
      if (ownKey) sessionStorage.removeItem(ownKey);
    } catch {
      // private mode / storage disabled
    }
    window.removeEventListener('scroll', onScroll);
    ownKey = null;
  });
}
